import { advanceOf } from './textMetrics';
import { useEffect, useRef, useState } from 'react';
import { DEFAULT_FONT, OBJECT_ICONS, SHAPES, shapeDefaults } from './editorCatalog';
import { applyProductMetrics, areaOf, textRotation } from './model';
import { newId } from './newId';
import { garmentColor, viewDef } from './textileChecks';

const STORAGE_KEY = 'manucreator-designer-v2';
// AI Typography: a new text keeps its look and is shrunk/grown to stay inside its layout box.
const fitTypography = (o, patch) => {
  const box = o.meta?.typo_box;
  if (o.type !== 'text' || patch.text === undefined || !box?.box_w) return o;
  const text = o.uppercase ? String(o.text || '').toUpperCase() : String(o.text || ''), longest = Math.max(1, ...text.split('\n').map(l => advanceOf(l, o.font) * (1 + (o.letterSpacing || 0))));
  return { ...o, scale: Math.min(box.max_scale || o.scale || 1, box.box_w / longest) };
};

export const readStored = () => { try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || null; } catch { return null; } };
export const starterObjects = (product, text = 'Dein Unikat') => {
  const a = areaOf(product), rotation = textRotation(product), long = rotation ? a.h : a.w;
  return [{ id: 'text', type: 'text', name: text, text: text.slice(0, product.max_text || 60), curve: 0, font: DEFAULT_FONT, x: a.cx, y: a.cy, scale: Math.max(.45, Math.min(1.4, long / 420)), rotation, visible: true, locked: false, strokeWidthMm: .4, outline: false }];
};
const names = { text: 'Dein Text', image: 'Mein Bild', decoration: 'Motiv' };
const sane = list => Array.isArray(list) && list.every(o => o?.id && Object.hasOwn(OBJECT_ICONS, o.type));

export const useEditor = (product, initial, options = {}) => {
  const persist = options.persist !== false;
  const [objects, setObjects] = useState(() => sane(initial?.objects) ? initial.objects.map(o => ({ ...shapeDefaults(o.type), ...o })) : starterObjects(product));
  const textile = product.kind === 'textile', multi = (product.views || []).length > 0;
  const [view, setView] = useState('front');
  const [meta, setMeta] = useState(initial?.meta || {});
  const garment = textile ? garmentColor(product, meta) : null;
  const productFor = v => {
    const d = multi ? viewDef(product, v) : null, own = garment?.image_file_ids?.[v || 'front'];
    const base = d ? { ...product, area: d.area, area_width_mm: d.area_width_mm, area_height_mm: d.area_height_mm, text_direction: d.text_direction || 'auto', image: d.image_file_id ? `/api/studio/product-images/${d.image_file_id}` : product.image, exclusions: [] } : product;
    return own ? { ...base, image: `/api/studio/product-images/${own}`, garmentOwnPhoto: true } : base;
  };
  const viewProduct = productFor(view);
  applyProductMetrics(viewProduct);
  const onView = v => multi ? objects.filter(o => (o.meta?.view || 'front') === v) : objects;
  const shown = onView(view);
  const current = useRef(objects), gesture = useRef(null);
  current.current = objects;
  const [selectedIds, setSelectedIds] = useState(() => { const first = objects.find(o => o.type === 'text')?.id || objects[0]?.id; return first ? [first] : []; });
  const selectedId = selectedIds[selectedIds.length - 1] || null;
  // Grouped elements (meta.group_id) are always selected together; the clicked one stays the primary selection.
  const mates = id => { const g = current.current.find(o => o.id === id)?.meta?.group_id; return g ? current.current.filter(o => o.meta?.group_id === g).map(o => o.id) : [id]; };
  const setSelectedId = id => setSelectedIds(id ? [...mates(id).filter(x => x !== id), id] : []);
  const toggleSelect = id => setSelectedIds(ids => { const g = mates(id); return ids.includes(id) ? ids.filter(x => !g.includes(x)) : [...ids.filter(x => !g.includes(x)), ...g.filter(x => x !== id), id]; });
  const [hoverId, setHoverId] = useState(null);
  const selectMany = ids => setSelectedIds([...new Set(ids)]);
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [saveState, setSaveState] = useState('saved');
  const [swap, setSwap] = useState(null);
  useEffect(() => {
    if (!persist) return;
    setSaveState('saving');
    const timeout = setTimeout(() => {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ product_id: product.id, objects, meta })); setSaveState('saved'); }
      catch { setSaveState('unavailable'); }
    }, 650);
    return () => clearTimeout(timeout);
  }, [objects, meta, product.id, persist]);
  const update = (updater, record = true) => {
    const old = current.current;
    const next = typeof updater === 'function' ? updater(old) : updater;
    if (JSON.stringify(old) === JSON.stringify(next)) return;
    if (record) { setPast(p => [...p.slice(-49), old]); setFuture([]); }
    current.current = next;
    setObjects(next);
  };
  const begin = () => { if (!gesture.current) gesture.current = current.current; };
  const end = () => {
    if (gesture.current && JSON.stringify(gesture.current) !== JSON.stringify(current.current)) {
      const snapshot = gesture.current;
      setPast(p => [...p.slice(-49), snapshot]); setFuture([]);
    }
    gesture.current = null;
  };
  const patchObject = (id, patch, record = true) => update(old => old.map(o => o.id === id ? fitTypography({ ...o, ...patch }, patch) : o), record);
  const patchMany = (patches, record = true) => update(old => old.map(o => patches[o.id] ? { ...o, ...patches[o.id] } : o), record);
  const undo = () => {
    if (!past.length) return;
    const prev = past[past.length - 1], old = current.current;
    setFuture(f => [old, ...f]); setPast(p => p.slice(0, -1));
    current.current = prev; setObjects(prev);
  };
  const redo = () => {
    if (!future.length) return;
    const next = future[0], old = current.current;
    setPast(p => [...p, old]); setFuture(f => f.slice(1));
    current.current = next; setObjects(next);
  };
  const add = (type, extra = {}) => {
    if (onView(view).length >= 12) return null;
    const a = areaOf(viewProduct);
    const item = { id: `${type}-${newId()}`, type, name: SHAPES.find(s => s.type === type)?.label || names[type], x: a.cx, y: a.cy, scale: 1, rotation: type === 'text' ? textRotation(viewProduct) : 0, visible: true, locked: false, ...shapeDefaults(type), ...extra, ...(textile ? { meta: { color: '#111111', ...(extra.meta || {}), view } } : multi ? { meta: { ...(extra.meta || {}), view } } : {}) };
    update(old => [...old, item]); setSelectedId(item.id); return item.id;
  };
  // Copies keep relative positions; a copied group becomes a new group. `target` = side for pasting from the clipboard.
  const copiesOf = (sources, dx, dy, target) => { const regroup = {}; return sources.map(source => { const meta = source.meta ? Object.fromEntries(Object.entries(source.meta).filter(([k]) => !k.startsWith('template_') && !k.startsWith('field_'))) : undefined; if (meta?.group_id) meta.group_id = regroup[meta.group_id] ||= `g-${newId()}`; if (meta && target && multi) meta.view = target; return { ...source, id: `${source.type}-${newId()}`, name: `${source.name} Kopie`.slice(0, 60), x: source.x + dx, y: source.y + dy, locked: false, meta }; }); };
  const duplicate = (ids = selectedIds) => {
    const sources = current.current.filter(o => ids.includes(o.id));
    if (!sources.length || onView(view).length + sources.length > 12) return false;
    const copies = copiesOf(sources, 15, 20);
    update(old => [...old, ...copies]); selectMany(copies.map(c => c.id)); return true;
  };
  const paste = list => {
    if (!list?.length || onView(view).length + list.length > 12) return false;
    const copies = copiesOf(list, 12, 12, view);
    update(old => [...old, ...copies]); selectMany(copies.map(c => c.id)); return true;
  };
  const remove = (ids = selectedIds) => { const doomed = current.current.filter(o => ids.includes(o.id) && !o.locked).map(o => o.id); update(old => old.filter(o => !doomed.includes(o.id))); setSelectedIds(s => s.filter(x => !doomed.includes(x))); };
  const group = () => { if (selectedIds.length < 2) return; const g = `g-${newId()}`; update(old => old.map(o => selectedIds.includes(o.id) ? { ...o, meta: { ...(o.meta || {}), group_id: g } } : o)); };
  const ungroup = () => update(old => old.map(o => selectedIds.includes(o.id) && o.meta?.group_id ? { ...o, meta: Object.fromEntries(Object.entries(o.meta).filter(([k]) => k !== 'group_id')) } : o));
  // Moves `ids` (kept in their order) directly above (`after`) or below the element `targetId` in the stacking order.
  const moveLayers = (ids, targetId, after) => update(old => {
    const picked = old.filter(o => ids.includes(o.id)), rest = old.filter(o => !ids.includes(o.id)), at = rest.findIndex(o => o.id === targetId);
    if (at < 0) return old;
    const i = after ? at + 1 : at; return [...rest.slice(0, i), ...picked, ...rest.slice(i)];
  });
  const reorder = (id, target) => update(old => {
    const list = [...old]; const index = list.findIndex(o => o.id === id);
    if (index < 0) return old;
    const [item] = list.splice(index, 1);
    list.splice(Math.max(0, Math.min(target, list.length)), 0, item); return list;
  });
  const stack = (ids, dir) => {
    const list = onView(view), first = list.findIndex(o => ids.includes(o.id)), last = list.map(o => ids.includes(o.id)).lastIndexOf(true), rest = list.filter(o => !ids.includes(o.id));
    if (first < 0 || !rest.length) return;
    const target = dir === 'front' ? [rest[rest.length - 1], true] : dir === 'back' ? [rest[0], false] : dir === 'forward' ? [list.slice(last + 1).find(o => !ids.includes(o.id)), true] : [list.slice(0, first).reverse().find(o => !ids.includes(o.id)), false];
    if (target[0]) moveLayers(ids, target[0].id, target[1]);
  };
  const replace = (nextObjects, nextMeta = {}) => { update(nextObjects); setMeta(nextMeta); setSelectedId(nextObjects.find(o => o.type === 'text')?.id || nextObjects[0]?.id || null); };
  const getObject = id => current.current.find(o => o.id === id);
  const live = objects.filter(o => selectedIds.includes(o.id));
  const api = { product: viewProduct, baseProduct: product, textile, multi, sides: multi ? [{ id: 'front', name: 'Vorderseite' }, ...product.views] : [], garment, view, setView: v => { setView(v); setSelectedId(null); }, allObjects: objects, objects: shown, meta, setMeta: patch => setMeta(old => ({ ...old, ...patch })), selectedId, selectedIds: live.map(o => o.id), selectedObjects: live, select: setSelectedId, toggleSelect, selectMany, selected: objects.find(o => o.id === selectedId), getObject, saveState, update, patchObject, patchMany, begin, end, undo, redo, canUndo: !!past.length, canRedo: !!future.length, add, duplicate, paste, remove, group, ungroup, moveLayers, stack, reorder, replace, swap, setSwap, hoverId, setHover: setHoverId };
  api.forView = v => ({ ...api, product: productFor(v), objects: onView(v), selectedIds: [], selectedObjects: [], selected: null, hoverId: null });
  return api;
};
